import React, { useEffect, useState, useRef } from 'react';
import { X, Loader, Maximize2, Minimize2, ZoomIn, ZoomOut, Maximize, RotateCcw, AlertTriangle } from 'lucide-react';
import cytoscape from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';
import { transformDataToCytoscape, getHealthColor } from '../../utils/cytoscapeTransform';
import { createCytoscapeConfig, layoutConfig } from '../../utils/cytoscapeConfig';
import api from '../../utils/api';

// Register the layout
cytoscape.use(coseBilkent);

const NetworkMapModal = ({ item, onClose }) => {
  const cyRef = useRef(null);
  const cyInstance = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkData, setNetworkData] = useState(null);

  // Fetch network visualization data from API
  useEffect(() => {
    const fetchNetworkData = async () => {
      if (!item || !item.changeNumber) {
        setError('No change number provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log(`[NetworkMapModal] Fetching network data for ${item.changeNumber}`);
        
        // Fetch from API endpoint
        const data = await api.getNetworkVisualization(item.changeNumber);
        
        console.log(`[NetworkMapModal] Received network data:`, data);
        setNetworkData(data);
        setLoading(false);
      } catch (err) {
        console.error('[NetworkMapModal] Failed to fetch network data:', err);
        
        // Check if it's an HTML response (CloudFront routing issue)
        if (err.message && err.message.includes('Unexpected token')) {
          setError('Network visualization endpoint not properly configured. Please contact support.');
        } else if (err.message && err.message.includes('404')) {
          setError('No network data available for this change');
        } else {
          setError(err.message || 'Failed to load network visualization');
        }
        
        setLoading(false);
      }
    };

    fetchNetworkData();
  }, [item]);

  // Initialize Cytoscape when network data is available
  useEffect(() => {
    if (!networkData || !cyRef.current || loading || error) return;

    try {
      // Transform the API response to Cytoscape format
      // If API returns the data in the expected format, use it directly
      // Otherwise, fall back to transforming from item data
      let nodes, edges;
      
      if (networkData.nodes && networkData.edges) {
        // API returned Cytoscape-formatted data
        nodes = networkData.nodes;
        edges = networkData.edges;
      } else {
        // Transform from raw API data
        const transformed = transformDataToCytoscape([networkData]);
        nodes = transformed.nodes;
        edges = transformed.edges;
      }

      if (nodes.length === 0) {
        setError('No network data available for this change');
        return;
      }

      console.log(`[NetworkMapModal] Rendering ${nodes.length} nodes and ${edges.length} edges`);

      // Create Cytoscape configuration using shared config
      const config = createCytoscapeConfig(cyRef.current, nodes, edges);

      // Initialize Cytoscape instance
      const cy = cytoscape(config);

      // Node click handler
      cy.on('tap', 'node', (evt) => {
        const node = evt.target;
        setSelectedNode(node.data());
      });

      // Background click handler
      cy.on('tap', (evt) => {
        if (evt.target === cy) {
          setSelectedNode(null);
        }
      });

      cyInstance.current = cy;

      // Cleanup function
      return () => {
        if (cyInstance.current) {
          cyInstance.current.destroy();
          cyInstance.current = null;
        }
      };
    } catch (err) {
      console.error('[NetworkMapModal] Failed to initialize Cytoscape:', err);
      setError('Failed to render network visualization');
    }
  }, [networkData, loading, error]);

  // Zoom controls
  const handleZoomIn = () => {
    if (cyInstance.current) {
      cyInstance.current.zoom(cyInstance.current.zoom() * 1.2);
      cyInstance.current.center();
    }
  };

  const handleZoomOut = () => {
    if (cyInstance.current) {
      cyInstance.current.zoom(cyInstance.current.zoom() * 0.8);
      cyInstance.current.center();
    }
  };

  const handleFitView = () => {
    if (cyInstance.current) {
      cyInstance.current.fit(null, 50);
    }
  };

  const handleResetLayout = () => {
    if (cyInstance.current) {
      cyInstance.current.layout(layoutConfig).run();
    }
  };

  if (!item) return null;

  // Calculate relationship counts from network data or fallback to item data
  const upstream = networkData?.relationships?.upstream || item.raw_data?.relationships?.upstream || [];
  const downstream = networkData?.relationships?.downstream || item.raw_data?.relationships?.downstream || [];
  const totalRelationships = upstream.length + downstream.length;

  // Retry handler
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    // Trigger re-fetch by updating a dependency
    window.location.reload();
  };

  return (
    <div className={`fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center ${isFullscreen ? 'p-0' : 'p-4'}`}
         onClick={onClose}>
      <div className={`bg-dark-card border border-orange-accent/40 rounded-xl w-full overflow-hidden ${isFullscreen ? 'h-full max-w-full' : 'max-w-6xl max-h-[90vh]'}`}
           style={{ boxShadow: '0 0 60px rgba(255, 107, 53, 0.3)' }}
           onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-accent/20 to-purple-accent/20 border-b border-orange-accent/30 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-orange-accent mb-1"
                style={{ textShadow: '0 0 20px #ff6b35' }}>
              Network Map: {item.changeNumber}
            </h2>
            <p className="text-gray-400 text-sm">
              {loading ? 'Loading...' : error ? 'Error loading data' : `${totalRelationships} total relationships`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 rounded-lg bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 transition-all"
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-orange-accent hover:border-orange-accent/40 transition-all"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`${isFullscreen ? 'h-[calc(100vh-80px)]' : 'h-[600px]'}`}>
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center h-full">
              <Loader size={48} className="text-cyan-accent animate-spin mb-4" />
              <p className="text-gray-400">Loading network visualization...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center h-full p-8">
              <AlertTriangle size={48} className="text-orange-accent mb-4" />
              <p className="text-gray-400 mb-4 text-center max-w-md">{error}</p>
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent rounded hover:bg-cyan-accent/30 transition-all"
              >
                Retry
              </button>
            </div>
          )}

          {/* Visualization Content */}
          {!loading && !error && (
            <div className="h-full flex relative">
              {/* Cytoscape Graph Container */}
              <div className={`${selectedNode ? 'flex-1' : 'w-full'} bg-black/40 relative transition-all`}>
                <div ref={cyRef} className="w-full h-full" />
                
                {/* Zoom Controls */}
                <div className="absolute bottom-4 right-4 flex flex-col gap-2">
                  <button
                    onClick={handleZoomIn}
                    className="p-2 rounded-lg bg-dark-card/90 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 transition-all backdrop-blur-sm"
                    title="Zoom In"
                  >
                    <ZoomIn size={20} />
                  </button>
                  <button
                    onClick={handleZoomOut}
                    className="p-2 rounded-lg bg-dark-card/90 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 transition-all backdrop-blur-sm"
                    title="Zoom Out"
                  >
                    <ZoomOut size={20} />
                  </button>
                  <button
                    onClick={handleFitView}
                    className="p-2 rounded-lg bg-dark-card/90 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 transition-all backdrop-blur-sm"
                    title="Fit View"
                  >
                    <Maximize size={20} />
                  </button>
                  <button
                    onClick={handleResetLayout}
                    className="p-2 rounded-lg bg-dark-card/90 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 transition-all backdrop-blur-sm"
                    title="Reset Layout"
                  >
                    <RotateCcw size={20} />
                  </button>
                </div>
              </div>

              {/* Side Panel - Node Details */}
              {selectedNode && (
                <div className="w-80 bg-dark-border/20 border-l border-dark-border/60 p-4 overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-cyan-accent">Node Details</h3>
                    <button
                      onClick={() => setSelectedNode(null)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Label</div>
                      <div className="text-white font-semibold">{selectedNode.label || 'Unknown'}</div>
                    </div>
                    
                    {selectedNode.description && (
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Description</div>
                        <div className="text-white text-sm">{selectedNode.description}</div>
                      </div>
                    )}
                    
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Type</div>
                      <div className="text-white">{selectedNode.type || 'N/A'}</div>
                    </div>
                    
                    {selectedNode.direction && (
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Direction</div>
                        <div className="text-white capitalize">{selectedNode.direction}</div>
                      </div>
                    )}
                    
                    {selectedNode.health && (
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Health Status</div>
                        <div className={`font-semibold ${
                          selectedNode.health === 'HEALTHY' ? 'text-green-500' :
                          selectedNode.health === 'DEGRADED' ? 'text-yellow-500' :
                          selectedNode.health === 'UNHEALTHY' ? 'text-red-500' : 'text-gray-500'
                        }`}>
                          {selectedNode.health}
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Health Score</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-dark-border rounded-full overflow-hidden">
                          <div 
                            className="h-full transition-all"
                            style={{ 
                              width: `${selectedNode.healthScore || 0}%`,
                              backgroundColor: getHealthColor(selectedNode.healthScore || 0)
                            }}
                          />
                        </div>
                        <span className="text-white font-semibold">{Math.round(selectedNode.healthScore || 0)}</span>
                      </div>
                    </div>
                    
                    {selectedNode.isRoot && (
                      <div className="mt-4 p-3 bg-cyan-accent/10 border border-cyan-accent/30 rounded">
                        <div className="text-cyan-accent text-sm font-semibold">Main Change Node</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Legend - Only show when not in fullscreen and not loading and no error */}
        {!isFullscreen && !loading && !error && (
          <div className="border-t border-dark-border/50 p-4 bg-dark-border/10">
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#00e5ff', boxShadow: '0 0 10px #00e5ff' }}></div>
                <span className="text-gray-400">Main Change</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#b24bf3', boxShadow: '0 0 10px #b24bf3' }}></div>
                <span className="text-gray-400">Upstream ({upstream.length})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#ff6b35', boxShadow: '0 0 10px #ff6b35' }}></div>
                <span className="text-gray-400">Downstream ({downstream.length})</span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-gray-500 text-xs">💡 Drag nodes • Scroll to zoom • Click for details</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkMapModal;
